import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSessionPicker } from '../../hooks/booking/useSessionPicker'
import { PassOwner, useBookingPricing } from '../../hooks/booking/useBookingPricing'
import { CheckoutMode, useStartCheckout } from '../../hooks/booking/useStartCheckout'
import { apiMessageKey } from '../../hooks/useApiRequest'

import PageHeader from '../../components/ui/pageHeader/PageHeader'
import SummaryPanel from '../../components/authenticated/booking/summaryPanel/SummaryPanel'
import BookingLegend from '../../components/authenticated/booking/bookingLegend/BookingLegend'
import SessionPicker, { SessionPickerDays, SessionPickerSetup } from '../../components/authenticated/booking/sessionPicker/SessionPicker'
import FundingOptions from '../../components/authenticated/booking/fundingOptions/FundingOptions'
import BookingPageLayout from '../../components/bookingPageLayout/BookingPageLayout'
import EmailConfirmationNotice from '../../components/authenticated/booking/emailConfirmationNotice/EmailConfirmationNotice'

import { useApp } from '../../contexts/AppContext'
import { useProfileContext } from '../../contexts/ProfileContext'
import { BannerKind } from '../../types/contexts/AppContext'
import { BookingLocationState } from '../../types/booking/booking'
import { formatMoney } from '../../utils/booking/booking'

const SELF: PassOwner = { self: true }

const Booking = () => {
  const { profile } = useProfileContext()
  // Booking opens once the user confirms their email (the backend refuses checkouts until then): the courts stay
  // visible but cannot be picked, and the summary is hidden.
  const emailPending = profile?.emailConfirmed === false
  const { showBanner } = useApp()
  const { t } = useTranslation('bookings')
  const location = useLocation()
  const navigate = useNavigate()
  const [initialState] = useState(() => location.state as BookingLocationState | null)
  const draft = initialState?.bookingDraft
  const resumed = useRef(false)

  const [topUpPassProductId, setTopUpPassProductId] = useState<number | undefined>(
    draft?.topUpPassProductId
  )

  const picker = useSessionPicker({ initial: draft })
  const pricing = useBookingPricing({
    sessions: picker.selected,
    clubId: picker.clubId,
    passOwner: SELF,
    topUpPassProductId,
  })
  const { startCheckout } = useStartCheckout()

  useEffect(() => {
    if (initialState) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // A top-up chosen for a previous selection may no longer be offered.
    if (
      topUpPassProductId !== undefined &&
      pricing.ready &&
      !pricing.plan.topUpOptions.some(option => option.id === topUpPassProductId)
    ) {
      setTopUpPassProductId(undefined)
    }
  }, [pricing.ready, pricing.plan.topUpOptions, topUpPassProductId])

  useEffect(() => {
    if (picker.error) {
      showBanner({ kind: BannerKind.ERR, msg: picker.error })
      picker.resetError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.error])

  const handleConfirm = async () => {
    if (picker.selected.length === 0 || pricing.quoting) {
      return
    }
    // The server prices the selection once, here; the confirmation step shows its quote.
    let confirmed
    try {
      confirmed = await pricing.confirmQuote()
    } catch (caught) {
      if (apiMessageKey(caught) === 'availability-conflict') {
        picker.reload()
      }
      showBanner({ kind: BannerKind.ERR, msg: caught instanceof Error ? caught.message : String(caught) })
      return
    }
    startCheckout({
      mode: CheckoutMode.CUSTOMER,
      sessions: picker.selected,
      lines: confirmed.lines,
      total: confirmed.total,
      pointsAmount: confirmed.pointsAmount,
      topUpPassProductId,
      topUpName: confirmed.quote.topUpOptions.find(option => option.id === topUpPassProductId)?.name,
      returnTo: '/booking',
      returnState: {
        bookingDraft: { ...picker.draft, topUpPassProductId },
      } satisfies BookingLocationState,
      onConflict: picker.reload,
    })
  }

  useEffect(() => {
    // Back from completing the billing profile: continue to the confirmation once the passes are known.
    if (initialState?.resumeConfirmation && !resumed.current && picker.selected.length > 0 && pricing.ready) {
      resumed.current = true
      handleConfirm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing.ready])

  const total = pricing.amountDue
  const confirmLabel =
    total && total.amount > 0
      ? t('booking.booking-courts.summary-panel.pay', { total: formatMoney(total) })
      : t('booking.booking-courts.summary-panel.confirm')

  return (
    <BookingPageLayout
      header={
        <>
          <PageHeader
            title={{
              eyebrow: t('booking.page-header.eyebrow'),
              h1: t('booking.page-header.title'),
            }}
            sub={t('booking.page-header.sub.paid')}
          />
          {emailPending && <EmailConfirmationNotice />}
        </>
      }
      toolbar={<SessionPickerSetup picker={picker} />}
      days={<SessionPickerDays picker={picker} />}
      courts={
        <SessionPicker picker={picker} showDays={false} disabled={emailPending} isIncluded={pricing.isIncluded} />
      }
      legend={<BookingLegend />}
      summary={
        emailPending ? undefined : (
          <SummaryPanel
            lines={pricing.lines}
            total={total}
            pointsAmount={pricing.pointsAmount}
            confirmLabel={confirmLabel}
            confirmDisabled={!pricing.ready}
            loading={pricing.quoting}
            totalSubtitle={t('booking.booking-courts.summary-panel.charge-today')}
            onConfirm={handleConfirm}
            onClear={picker.clear}
            options={
              picker.selected.length > 0 ? (
                <FundingOptions
                  plan={pricing.plan}
                  topUpPassProductId={topUpPassProductId}
                  onTopUpChange={setTopUpPassProductId}
                  disabled={pricing.quoting}
                />
              ) : undefined
            }
          />
        )
      }
    />
  )
}

export default Booking
